import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/PaymentMasterPage.css';
import { API_BASE_URL } from '../services/api';

const PAYMENT_METHODS = ['RTGS', 'CEFT', 'SLIPS', 'CHEQUE', 'CBSL'];

const initialForm = {
  PaymentMethod: '',
  PaymentMethodOwner: '',
  PaymentMethodCode: '',
  BankPaymentCode: ''
};

export default function PaymentMasterPage() {
  const [tab, setTab] = useState('add');
  const [form, setForm] = useState(initialForm);
  const [bankPaymentCodes, setBankPaymentCodes] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search / list state
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // View / Edit modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [editForm, setEditForm] = useState(initialForm);
  const [editMessage, setEditMessage] = useState('');

  // Fetch all payment methods for search
  const fetchPayments = async (term = '') => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/payment-master/search`, { params: { PaymentMethod: term } });
      const rows = res.data.data || [];
      // Normalise keys to match form shape
      const normalised = rows.map(row => ({
        id: row.id,
        PaymentMethod: row.payment_method || row.PaymentMethod,
        PaymentMethodOwner: row.payment_method_owner || row.PaymentMethodOwner,
        PaymentMethodCode: row.payment_method_code || row.PaymentMethodCode,
        BankPaymentCode: row.bank_payment_code || row.BankPaymentCode,
        created_at: row.created_at
      }));
      setSearchResults(normalised);
    } catch (e) {
      setError('Failed to fetch payment methods');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'search') fetchPayments();
  }, [tab]);

  // Fetch bank payment codes on mount
  useEffect(() => {
    const fetchBankPaymentCodes = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/payment-master/bank-payment-codes`);
        setBankPaymentCodes(res.data.data || []);
      } catch (err) {
        setBankPaymentCodes([]);
      }
    };
    fetchBankPaymentCodes();
  }, []);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');

    if (name === 'BankPaymentCode') {
      if (value) {
        try {
          // Remove any :1 or similar suffix from value
          const cleanValue = value.split(':')[0];
          // FIXED: Use API_BASE_URL consistently
          const res = await axios.get(`${API_BASE_URL}/payment-master/bank-details/${cleanValue}`);
          setBankDetails(res.data.data);
        } catch (err) {
          setBankDetails(null);
        }
      } else {
        setBankDetails(null);
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await axios.post(`${API_BASE_URL}/payment-master`, form);
      setSuccess('Saved successfully!');
      setForm(initialForm);
      fetchPayments();
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setError('Payment Method or Code already exists!');
      } else {
        setError('Failed to save payment method');
      }
    }
  };

  // Search logic
  const handleSearch = (e) => {
    e.preventDefault();
    fetchPayments(searchTerm);
  };

  // Open view modal
  const openViewModal = (pm) => {
    setSelectedPayment(pm);
    setViewModalOpen(true);
    setEditModalOpen(false);
  };

  // Open edit modal
  const openEditModal = (pm) => {
    setSelectedPayment(pm);
    setEditForm({
      PaymentMethod: pm.PaymentMethod,
      PaymentMethodOwner: pm.PaymentMethodOwner,
      PaymentMethodCode: pm.PaymentMethodCode,
      BankPaymentCode: pm.BankPaymentCode
    });
    setEditMessage('');
    setEditModalOpen(true);
    setViewModalOpen(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
    setEditMessage('');
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!selectedPayment) return;
    try {
      await axios.put(`${API_BASE_URL}/payment-master/${selectedPayment.id}`, editForm);
      setEditMessage('Updated successfully!');
      await fetchPayments(searchTerm);
      setEditModalOpen(false);
      setSelectedPayment(null);
    } catch (err) {
      setEditMessage('Failed to update payment method');
    }
  };

  return (
    <div className="payment-master-page ui-page">
      <div className="tabs">
        <button
          className={tab === 'add' ? 'active' : ''}
          onClick={() => {
            setTab('add');
            setError('');
            setSuccess('');
          }}
        >
          Add Pay Med
        </button>
        <button
          className={tab === 'search' ? 'active' : ''}
          onClick={() => {
            setTab('search');
            setError('');
            setSuccess('');
            fetchPayments();
          }}
        >
          Search / Manage PayMed
        </button>
      </div>
      {tab === 'add' && (
        <form className="add-payment-form" onSubmit={handleSave}>
          <div className="form-row">
            <label>Payment Method</label>
            <select name="PaymentMethod" value={form.PaymentMethod} onChange={handleChange} required>
              <option value="">Select...</option>
              {PAYMENT_METHODS.map(pm => (
                <option key={pm} value={pm}>{pm}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>Payment Method Owner</label>
            <input name="PaymentMethodOwner" value={form.PaymentMethodOwner} onChange={handleChange} required />
          </div>
          <div className="form-row">
            <label>Payment Method Code</label>
            <input name="PaymentMethodCode" value={form.PaymentMethodCode} onChange={handleChange} required />
          </div>
          <div className="form-row">
            <label>Bank Payment Code</label>
            <select name="BankPaymentCode" value={form.BankPaymentCode} onChange={handleChange} required>
              <option value="">Select...</option>
              {bankPaymentCodes.map(code => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
          {bankDetails && (
            <div className="form-row">
              <label>Bank Name</label>
              <input value={bankDetails.bank_name || ''} readOnly />
            </div>
          )}
          {bankDetails && (
            <div className="form-row">
              <label>Bank Branch</label>
              <input value={bankDetails.bank_branch || ''} readOnly />
            </div>
          )}
          {bankDetails && (
            <div className="form-row">
              <label>Bank Account Number</label>
              <input value={bankDetails.bank_account_number || ''} readOnly />
            </div>
          )}
          <button type="submit" className="verify-save-btn">Verify & Save</button>
          {error && <div className="error-msg">{error}</div>}
          {success && <div className="success-msg">{success}</div>}
        </form>
      )}
      {tab === 'search' && (
        <div className="search-payment">
          <form className="search-bar" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search by method, owner or code"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </form>

          <div className="results-table-wrapper">
            {searchResults.length === 0 && !loading && (
              <div className="empty-state">No payment methods found.</div>
            )}
            {searchResults.length > 0 && (
              <table className="payment-master-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Owner</th>
                    <th>Code</th>
                    <th>Bank Pay Code</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map(pm => (
                    <tr key={pm.id}>
                      <td>{pm.PaymentMethod}</td>
                      <td>{pm.PaymentMethodOwner}</td>
                      <td>{pm.PaymentMethodCode}</td>
                      <td>{pm.BankPaymentCode}</td>
                      <td>
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => openViewModal(pm)}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => openEditModal(pm)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* View Modal */}
          {viewModalOpen && selectedPayment && (
            <div className="pm-modal-backdrop">
              <div className="pm-modal">
                <h3>View Payment Method</h3>
                <div className="modal-row">
                  <label>Payment Method</label>
                  <input value={selectedPayment.PaymentMethod} readOnly />
                </div>
                <div className="modal-row">
                  <label>Owner</label>
                  <input value={selectedPayment.PaymentMethodOwner} readOnly />
                </div>
                <div className="modal-row">
                  <label>Method Code</label>
                  <input value={selectedPayment.PaymentMethodCode} readOnly />
                </div>
                <div className="modal-row">
                  <label>Bank Payment Code</label>
                  <input value={selectedPayment.BankPaymentCode} readOnly />
                </div>
                <button
                  type="button"
                  className="verify-save-btn"
                  onClick={() => setViewModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Edit Modal */}
          {editModalOpen && selectedPayment && (
            <div className="pm-modal-backdrop">
              <div className="pm-modal">
                <h3>Edit Payment Method</h3>
                <form onSubmit={handleEditSave}>
                  <div className="modal-row">
                    <label>Payment Method</label>
                    <select
                      name="PaymentMethod"
                      value={editForm.PaymentMethod}
                      onChange={handleEditChange}
                      required
                    >
                      <option value="">Select...</option>
                      {PAYMENT_METHODS.map(pm => (
                        <option key={pm} value={pm}>{pm}</option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-row">
                    <label>Payment Method Owner</label>
                    <input
                      name="PaymentMethodOwner"
                      value={editForm.PaymentMethodOwner}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="modal-row">
                    <label>Payment Method Code</label>
                    <input
                      name="PaymentMethodCode"
                      value={editForm.PaymentMethodCode}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="modal-row">
                    <label>Bank Payment Code</label>
                    <select
                      name="BankPaymentCode"
                      value={editForm.BankPaymentCode}
                      onChange={handleEditChange}
                      required
                    >
                      <option value="">Select...</option>
                      {bankPaymentCodes.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                  {editMessage && (
                    <div className="success-msg" style={{ marginBottom: 8 }}>
                      {editMessage}
                    </div>
                  )}
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() => {
                        setEditModalOpen(false);
                        setSelectedPayment(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="verify-save-btn">
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}